/**
 * The v1_10_0 Broadcasts status vocabulary: what a status *means* on screen.
 *
 * One broadcast has one status, and every surface — the list badge, the detail
 * header, the retract button, the counters block — has to agree on what that
 * status implies. That agreement lives here so a badge and a button can never
 * disagree about whether a broadcast is still in flight.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE IS AND IS NOT
 * ---------------------------------------------------------------------------
 * Pure and synchronous. No React, no storage, no clock — a status is a fact
 * about the row you were handed, not about the moment you asked. That is what
 * makes `statusMeta` safe to call during render and trivial to test.
 *
 * The status ids ARE the wire values (`status: "paused_reputation"` really is
 * that string). Field names are not this file's business: `statusMeta` takes an
 * already-DECODED row, so the only key it reads is one `broadcastDecode.js`
 * produced.
 */

/* ── Tones ────────────────────────────────────────────────────────────── */

/**
 * Tone name → the FULL Tailwind class string.
 *
 * Written out in full, never assembled as `bg-bc-${tone}-bg`. Tailwind scans
 * source text for literal class names, so a template-built class is not in the
 * stylesheet at all — the badge ships with no background and nobody notices
 * until production. The `bc-*` tokens land in `tailwind.config.js` next phase;
 * `light-orange` (#FFF0E8) is already there.
 */
export const TONE_CLASSES = {
  ok: "bg-bc-ok-bg text-bc-ok",
  warn: "bg-bc-warn-bg text-bc-warn",
  bad: "bg-bc-bad-bg text-bc-bad",
  neut: "bg-bc-neut-bg text-bc-neut",
  accent: "bg-light-orange text-bc-accent-text",
};

/* ── Status table ─────────────────────────────────────────────────────── */

/**
 * The eight statuses the API can return.
 *
 * `partial` is amber, not red: some recipients did get it, and painting a
 * mostly-successful send in the failure colour teaches authors to panic at a
 * result that is usually fine. `failed` and `paused_reputation` are the red
 * ones because both mean nobody else is receiving anything.
 */
const STATUS_TABLE = {
  draft: { label: "Draft", tone: "neut" },
  scheduled: { label: "Scheduled", tone: "accent" },
  sending: { label: "Sending", tone: "accent", polls: true },
  sent: { label: "Sent", tone: "ok" },
  partial: { label: "Partial", tone: "warn" },
  failed: { label: "Failed", tone: "bad" },
  retracted: { label: "Retracted", tone: "neut" },
  paused_reputation: { label: "Paused", tone: "bad", banner: true },
};

/** Statuses a human can still stop. See `isRetractable`. */
const RETRACTABLE_KEYS = ["scheduled", "sending", "partial", "sent"];

/** Statuses that have no numbers to show yet. See `showsCounters`. */
const COUNTERLESS_KEYS = ["draft", "scheduled", "unknown"];

/**
 * Page-level banner copy, keyed by status. Only statuses that need to explain
 * themselves above the fold appear here.
 */
export const BANNER_COPY = {
  paused_reputation:
    "Sending stopped automatically — the bounce or complaint rate crossed the safe threshold.",
};

/* ── Reading a status ─────────────────────────────────────────────────── */

/**
 * Turn `queued_for_review` into `Queued for review`.
 *
 * Sentence case, not title case: these are server words we are surfacing
 * unchanged, and title-casing them would dress an unrecognised value up as a
 * designed label.
 *
 * @param {string} raw
 * @returns {string}
 */
const humanize = (raw) => {
  const words = String(raw).replace(/_/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * @typedef {Object} StatusMeta
 * @property {string} key - The status id, or the raw value when unrecognised.
 * @property {string} label - What the badge reads.
 * @property {string} tone - A key of `TONE_CLASSES`.
 * @property {boolean} banner - Does this status warrant a page-level banner?
 * @property {boolean} polls - Should the detail screen keep refetching?
 */

/**
 * Resolve a decoded row (or a bare status string) into everything the UI needs
 * to render its state.
 *
 * Two rules earn their keep here:
 *
 * 1. `retracted` beats `status`. The API leaves the pre-retraction status in
 *    place, so a retracted broadcast still says `status: "sent"` — reading
 *    `status` alone shows a green "Sent" badge on something the author just
 *    pulled.
 *
 * 2. An unrecognised status renders itself. Falling through to "Draft" would
 *    make every state the server adds after we ship invisible — and "Draft" of
 *    all things, implying it had never been sent. An absent status is likewise
 *    "Unknown", never "Draft": we know
 *    nothing about this row, and claiming it is a draft is a claim.
 *
 * @param {Object|string|null|undefined} row - A decoded row, or a status.
 * @returns {StatusMeta}
 */
export const statusMeta = (row) => {
  const source = typeof row === "string" ? { status: row } : row || {};
  const raw = source.retracted ? "retracted" : source.status;
  const key = String(raw ?? "").trim();

  if (!key) return { key: "unknown", label: "Unknown", tone: "neut", banner: false, polls: false };

  const known = STATUS_TABLE[key];
  if (!known) return { key, label: humanize(key), tone: "neut", banner: false, polls: false };

  return {
    key,
    label: known.label,
    tone: known.tone,
    banner: known.banner === true,
    polls: known.polls === true,
  };
};

/**
 * Does this status have delivery numbers worth rendering?
 *
 * The single decision point behind the spec rule "a scheduled broadcast
 * renders — never 0; zero is a result, absence is not." A draft or a scheduled
 * send has not addressed anyone yet, so a `0 delivered` reads as a failure that
 * has not happened. Everything else — including `retracted` — has real numbers,
 * and a genuine zero there is information.
 *
 * @param {string} key - A `statusMeta().key`.
 * @returns {boolean}
 */
export const showsCounters = (key) => !COUNTERLESS_KEYS.includes(key);

/**
 * Can this broadcast still be retracted?
 *
 * `sent` qualifies because retraction also pulls the in-app entry, which
 * outlives delivery. A draft has nothing to retract, and retracting twice is
 * a 423 the user should never be offered.
 *
 * @param {Object|string|null|undefined} row - A decoded row, or a status.
 * @returns {boolean}
 */
export const isRetractable = (row) => {
  const { key } = statusMeta(row);
  return RETRACTABLE_KEYS.includes(key);
};
